import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery } from "@apollo/client"
import { useTranslation } from "react-i18next"
import PPErrorDisplay from "../components/layout/PPErrorDisplay"
import PPDematchingPairRow from "../components/dematching/PPDematchingPairRow"
import PPDematchingStatsSidebar from "../components/dematching/PPDematchingStatsSidebar"
import PPDematchingToolbar from "../components/dematching/PPDematchingToolbar"
import PPDematchingPairsSkeleton from "../components/dematching/PPDematchingPairsSkeleton"
import {
    MUTATION_DEMATCH_PAIR,
    MUTATION_REMATCH_PAIR,
    QUERY_DEMATCHING_PAIRS,
    QUERY_DEMATCHING_PRODUCERS,
    QUERY_DEMATCHING_CATEGORIES,
    QUERY_DEMATCHING_SHOPS
} from "../utils/dematching/dematchingQueries"
import PPUtils from "../utils/PPUtils"
import PPMatchingStatusBanner from "../components/matching/PPMatchingStatusBanner"
import "../components/matching/matching.css"
import "../components/dematching/dematching.css"

const PAGE_SIZE = 50
const STATS_MIN_SPLIT_WIDTH = 1040

function pairKey(pair) {
    return `${pair.shopId}-${pair.mappingId}-${pair.foreignProduct?.productNr}-${pair.customerProduct?.productNr}`
}

export default function PPDematching() {
    const [t] = useTranslation()

    const [shopId, setShopId] = useState(null)
    const [producerId, setProducerId] = useState(null)
    const [categoryDepartment, setCategoryDepartment] = useState(null)
    const [sortByPrice, setSortByPrice] = useState(false)
    const [onlyRemoved, setOnlyRemoved] = useState(false)
    const [page, setPage] = useState(0)
    const [jumpInput, setJumpInput] = useState("1")
    const [filtersExpanded, setFiltersExpanded] = useState(false)
    const [linkState, setLinkState] = useState({})
    const [priceInfoState, setPriceInfoState] = useState({})
    const [affectedShopsState, setAffectedShopsState] = useState({})
    const [togglingKey, setTogglingKey] = useState(null)
    const [narrowView, setNarrowView] = useState("list")
    const [isNarrowLayout, setIsNarrowLayout] = useState(false)

    const handleShopChange = useCallback((id) => {
        if (!PPUtils.isPositiveIntId(id)) {
            return
        }
        setShopId(id)
    }, [])

    const handleProducerChange = useCallback((id) => {
        if (id == null) {
            setProducerId(null)
            return
        }
        if (!PPUtils.isPositiveIntId(id)) {
            return
        }
        setProducerId(id)
    }, [])

    const pageBodyRef = useRef(null)
    const prevNarrowLayoutRef = useRef(false)
    const narrowViewRef = useRef("list")

    const [cachedTotalCount, setCachedTotalCount] = useState(0)

    const filter = useMemo(
        () => ({
            shopId: PPUtils.isPositiveIntId(shopId) ? shopId : null,
            producerId: PPUtils.isPositiveIntId(producerId) ? producerId : null,
            department: typeof categoryDepartment === "string" && categoryDepartment !== "" ? categoryDepartment : null,
            sortByPrice,
            onlyRemoved
        }),
        [shopId, producerId, categoryDepartment, sortByPrice, onlyRemoved]
    )

    const filterReady = PPUtils.isPositiveIntId(shopId)
    const hasCustomer = PPUtils.isValidCustomerId(PPUtils.getEffectiveCustomerId())
    const offset = page * PAGE_SIZE

    const { data: shopsData, loading: shopsLoading, error: shopsError, refetch: refetchShops } = useQuery(
        QUERY_DEMATCHING_SHOPS,
        { skip: !hasCustomer }
    )

    const { data: producersData, loading: producersLoading } = useQuery(QUERY_DEMATCHING_PRODUCERS, {
        variables: { shopId },
        skip: !filterReady
    })

    const { data: categoriesData, loading: categoriesLoading } = useQuery(QUERY_DEMATCHING_CATEGORIES, {
        variables: { shopId },
        skip: !filterReady
    })

    const {
        data: pairsData,
        loading: pairsLoading,
        error: pairsError
    } = useQuery(QUERY_DEMATCHING_PAIRS, {
        variables: { filter, offset, limit: PAGE_SIZE, includeTotalCount: page === 0 },
        skip: !filterReady,
        fetchPolicy: "cache-and-network"
    })

    const [dematchPair] = useMutation(MUTATION_DEMATCH_PAIR)
    const [rematchPair] = useMutation(MUTATION_REMATCH_PAIR)

    const pairs = pairsData?.dematchingPairs?.pairs ?? []
    const queryTotalCount = pairsData?.dematchingPairs?.totalCount

    useEffect(() => {
        const isUsableTotal = typeof queryTotalCount === "number" && Number.isFinite(queryTotalCount) && queryTotalCount >= 0
        if (isUsableTotal) {
            setCachedTotalCount(queryTotalCount)
        }
    }, [queryTotalCount])

    const isUsableQueryTotal = typeof queryTotalCount === "number" && Number.isFinite(queryTotalCount) && queryTotalCount >= 0
    let listTotalCount = 0
    if (cachedTotalCount > 0) {
        listTotalCount = cachedTotalCount
    } else if (isUsableQueryTotal) {
        listTotalCount = queryTotalCount
    }

    useEffect(() => {
        setCachedTotalCount(0)
    }, [shopId, producerId, categoryDepartment, sortByPrice, onlyRemoved])

    useEffect(() => {
        setCategoryDepartment(null)
    }, [shopId])

    useEffect(() => {
        const shops = shopsData?.dematchingShops
        if (shops?.length && shopId === null) {
            const firstShopId = shops[0]?.id
            if (PPUtils.isPositiveIntId(firstShopId)) {
                setShopId(firstShopId)
            }
        }
    }, [shopsData, shopId])

    useEffect(() => {
        setPage(0)
        setJumpInput("1")
    }, [shopId, producerId, categoryDepartment, sortByPrice, onlyRemoved])

    useEffect(() => {
        setJumpInput(String(page + 1))
    }, [page])

    useEffect(() => {
        narrowViewRef.current = narrowView
    }, [narrowView])

    useEffect(() => {
        const el = pageBodyRef.current
        if (!el) {
            return undefined
        }

        const updateLayout = (width) => {
            const narrow = width < STATS_MIN_SPLIT_WIDTH
            const wasNarrow = prevNarrowLayoutRef.current

            if (!wasNarrow && narrow) {
                setNarrowView("list")
            }

            prevNarrowLayoutRef.current = narrow
            setIsNarrowLayout(narrow)
        }

        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? el.getBoundingClientRect().width
            updateLayout(width)
        })
        observer.observe(el)
        updateLayout(el.getBoundingClientRect().width)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const collapseEl = document.getElementById("collapseDematchingFilter")
        if (!collapseEl) return undefined
        const onShown = () => setFiltersExpanded(true)
        const onHidden = () => setFiltersExpanded(false)
        collapseEl.addEventListener("shown.bs.collapse", onShown)
        collapseEl.addEventListener("hidden.bs.collapse", onHidden)
        return () => {
            collapseEl.removeEventListener("shown.bs.collapse", onShown)
            collapseEl.removeEventListener("hidden.bs.collapse", onHidden)
        }
    }, [])

    useEffect(() => {
        if (!pairs.length) {
            return
        }
        setLinkState((prev) => {
            const next = { ...prev }
            let changed = false
            pairs.forEach((pair) => {
                const key = pairKey(pair)
                if (next[key] === undefined) {
                    next[key] = pair.linked
                    changed = true
                }
            })
            return changed ? next : prev
        })
        setPriceInfoState((prev) => {
            const next = { ...prev }
            let changed = false
            pairs.forEach((pair) => {
                const key = pairKey(pair)
                if (next[key] === undefined && pair.priceTableJsonInfo) {
                    next[key] = pair.priceTableJsonInfo
                    changed = true
                }
            })
            return changed ? next : prev
        })
    }, [pairs])

    const shopOptions = useMemo(
        () =>
            (shopsData?.dematchingShops ?? []).map((s) => ({
                value: s.id,
                label: `${s.name} ${s.lang.toUpperCase()} (${s.matchedCount})`
            })),
        [shopsData]
    )

    const producerOptions = useMemo(
        () =>
            (producersData?.dematchingProducers ?? [])
                .filter((p) => p.id > 0 && p.name?.trim() && p.count > 0)
                .map((p) => ({
                    value: p.id,
                    label: `${p.name} (${p.count})`
                })),
        [producersData]
    )

    useEffect(() => {
        if (producerId && !producerOptions.some((o) => o.value === producerId)) {
            setProducerId(null)
        }
    }, [producerId, producerOptions])

    const categoryOptions = useMemo(
        () =>
            (categoriesData?.dematchingCategories ?? [])
                .filter((c) => c.name?.trim() && c.count > 0)
                .map((c) => ({ value: c.name, label: `${c.name} (${c.count})` })),
        [categoriesData]
    )

    useEffect(() => {
        if (categoryDepartment && !categoryOptions.some((o) => o.value === categoryDepartment)) {
            setCategoryDepartment(null)
        }
    }, [categoryDepartment, categoryOptions])

    const selectedShop = shopOptions.find((o) => o.value === shopId) ?? null
    const selectedShopRecord = shopsData?.dematchingShops?.find((s) => s.id === shopId)
    const selectedProducer = producerOptions.find((o) => o.value === producerId) ?? null
    const selectedCategory = categoryOptions.find((o) => o.value === categoryDepartment) ?? null

    const shopMatchedCount = shopsData?.dematchingShops?.find((s) => s.id === shopId)?.matchedCount ?? 0
    const producerMatchedCount = producerId
        ? producersData?.dematchingProducers?.find((p) => p.id === producerId)?.count
        : null
    const pairsQuerySettled = !pairsLoading && pairsData != null
    const matchedTotalForPaging =
        listTotalCount > 0 ? listTotalCount : producerMatchedCount ?? shopMatchedCount ?? 0
    const totalPages = Math.max(1, Math.ceil(matchedTotalForPaging / PAGE_SIZE))

    const handleJumpInputChange = useCallback((e) => {
        setJumpInput(e.target.value.replace(/\D/g, ""))
    }, [])

    const handleJump = useCallback(
        (e) => {
            e.preventDefault()
            if (totalPages <= 0) {
                return
            }
            const raw = jumpInput.trim()
            if (!raw) {
                return
            }
            const target = parseInt(raw, 10)
            if (Number.isNaN(target) || target < 1) {
                setPage(0)
                setJumpInput("1")
                return
            }
            const pageIndex = Math.min(totalPages - 1, target - 1)
            setPage(pageIndex)
        },
        [jumpInput, totalPages]
    )

    const shopQuickInfo = useMemo(() => {
        const shop = shopsData?.dematchingShops?.find((s) => s.id === shopId)
        if (!shop) {
            return null
        }
        return {
            id: shop.id,
            name: shop.name,
            lang: shop.lang,
            matchedCount: shop.matchedCount
        }
    }, [shopsData, shopId])

    const handleToggleLink = useCallback(
        async (pair) => {
            const key = pairKey(pair)
            const currentlyLinked = linkState[key] ?? pair.linked
            const priceTableJsonInfo = priceInfoState[key] ?? pair.priceTableJsonInfo ?? null

            setTogglingKey(key)
            try {
                if (currentlyLinked) {
                    if (pair.priceTableJsonInfo) {
                        setPriceInfoState((p) => ({ ...p, [key]: pair.priceTableJsonInfo }))
                    }
                    const result = await dematchPair({
                        variables: {
                            shopId: pair.shopId,
                            foreignProductNr: pair.foreignProduct.productNr,
                            customerProductNr: pair.customerProduct.productNr,
                            priceTableJsonInfo
                        }
                    })
                    const affectedShopIds = result.data?.dematchPair?.affectedShopIds
                    if (affectedShopIds?.length) {
                        setAffectedShopsState((s) => ({ ...s, [key]: affectedShopIds }))
                    } else {
                        setAffectedShopsState((s) => ({ ...s, [key]: [pair.shopId] }))
                    }
                    setLinkState((s) => ({ ...s, [key]: false }))
                } else {
                    await rematchPair({
                        variables: {
                            shopId: pair.shopId,
                            foreignProductNr: pair.foreignProduct.productNr,
                            customerProductNr: pair.customerProduct.productNr,
                            priceTableJsonInfo,
                            affectedShopIds: affectedShopsState[key] ?? [pair.shopId]
                        }
                    })
                    setLinkState((s) => ({ ...s, [key]: true }))
                }
                refetchShops()
            } catch (e) {
                // eslint-disable-next-line no-alert
                alert(e.message)
            } finally {
                setTogglingKey(null)
            }
        },
        [linkState, priceInfoState, affectedShopsState, dematchPair, rematchPair, refetchShops]
    )

    const showList = !isNarrowLayout || narrowView === "list"
    const showStats = !isNarrowLayout || narrowView === "stats"
    const isSplitLayout = !isNarrowLayout
    const isListLoading = filterReady && pairsLoading && pairs.length === 0
    const displayPage = matchedTotalForPaging > 0 ? page + 1 : 0

    if (!hasCustomer) {
        return (
            <div className="card card-outline card-primary">
                <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
                    <h3 className="card-title mb-0">{t("dematching.title")}</h3>
                </div>
                <div className="card-body">
                    <PPMatchingStatusBanner variant="no_customer" />
                </div>
            </div>
        )
    }

    if (shopsError) {
        return <PPErrorDisplay error={shopsError} />
    }

    return (
        <div className="card card-outline card-primary">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
                <h3 className="card-title mb-0">{t("dematching.title")}</h3>
            </div>
            <div className="card-body">
                <div
                    className={
                        "pp-matching-page" +
                        (isSplitLayout ? " pp-matching-page--split" : " pp-matching-page--main-only") +
                        (isNarrowLayout ? " pp-matching-page--narrow" : "") +
                        (isNarrowLayout && narrowView === "stats" ? " pp-matching-page--narrow-stats" : "")
                    }
                >
                    <div className="pp-matching-page__body" ref={pageBodyRef}>
                        {showList && (
                            <div className="pp-matching-page__main">
                                {isNarrowLayout && (
                                    <div className="pp-matching-view-switch">
                                        <button
                                            type="button"
                                            className="pp-matching-view-switch__btn btn btn-outline-primary btn-sm"
                                            onClick={() => setNarrowView("stats")}
                                        >
                                            <i className="fas fa-chart-bar" aria-hidden="true" />
                                            <span>{t("dematching.stats.show")}</span>
                                            <i className="fas fa-chevron-right pp-matching-view-switch__arrow" aria-hidden="true" />
                                        </button>
                                    </div>
                                )}

                                <PPDematchingToolbar
                                    shopOptions={shopOptions}
                                    selectedShop={selectedShop}
                                    onShopChange={handleShopChange}
                                    shopsLoading={shopsLoading}
                                    displayPage={displayPage}
                                    totalPages={totalPages}
                                    totalCount={matchedTotalForPaging}
                                    page={page}
                                    onPageChange={setPage}
                                    jumpInput={jumpInput}
                                    onJumpInputChange={handleJumpInputChange}
                                    onJump={handleJump}
                                    pairsLoading={pairsLoading}
                                    filtersExpanded={filtersExpanded}
                                    producerOptions={producerOptions}
                                    selectedProducer={selectedProducer}
                                    onProducerChange={handleProducerChange}
                                    producersLoading={producersLoading}
                                    categoryOptions={categoryOptions}
                                    selectedCategory={selectedCategory}
                                    onCategoryChange={setCategoryDepartment}
                                    categoriesLoading={categoriesLoading}
                                    filterReady={filterReady}
                                    sortByPrice={sortByPrice}
                                    onSortByPriceChange={setSortByPrice}
                                    onlyRemoved={onlyRemoved}
                                    onOnlyRemovedChange={setOnlyRemoved}
                                />

                                {pairsError && <PPErrorDisplay error={pairsError} />}

                                <div className="pp-dematching-list">
                                    {isListLoading && <PPDematchingPairsSkeleton rowCount={4} />}
                                    {!isListLoading &&
                                        pairs.map((pair) => {
                                            const key = pairKey(pair)
                                            const linked = linkState[key] ?? pair.linked
                                            return (
                                                <PPDematchingPairRow
                                                    key={key}
                                                    pair={pair}
                                                    priceLang={selectedShopRecord?.lang}
                                                    linked={linked}
                                                    toggling={togglingKey === key}
                                                    onToggleLink={() => handleToggleLink(pair)}
                                                />
                                            )
                                        })}
                                    {!isListLoading && pairs.length === 0 && filterReady && pairsQuerySettled && (
                                        <div className="pp-matching-status-banner">
                                            <div className="pp-matching-status-banner__title">
                                                {t("dematching.empty_title")}
                                            </div>
                                            <div className="pp-matching-status-banner__text">
                                                {t("dematching.empty_text")}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {showStats && (
                            <PPDematchingStatsSidebar
                                shopId={shopId}
                                shopQuickInfo={shopQuickInfo}
                                isNarrowLayout={isNarrowLayout}
                                onCollapse={() => setNarrowView("list")}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
